import { parseTimestamp } from '../hooks/useDeviceUrlSync'

/**
 * Pure helpers for Compare Mode chart groups: every selected series lives in
 * exactly one chart, and the layout is the ordered list of those charts. Also
 * the default-layout rule, URL (de)serialisation of layout and zoom window,
 * and the shared Y range for single-type charts.
 */

// Ordered charts, each an ordered list of series ids
export type CompareLayout = string[][]

// How the current layout was decided. Only a rule-picked split layout shows the
// one-time explainer; a layout from a shared link was chosen on purpose.
export type CompareLayoutSource = 'url' | 'rule' | 'user'

export interface ZoomWindow {
  min: number // ms
  max: number // ms
}

// Up to this many series open Combined; more open Separate.
export const COMBINED_DEFAULT_MAX_SERIES = 3


export function combineAll(ids: string[]): CompareLayout {
  return ids.length ? [[...ids]] : []
}

export function separateAll(ids: string[]): CompareLayout {
  return ids.map(id => [id])
}

// Up to 3 series open in one chart; more open one chart each.
export function defaultLayout(ids: string[]): CompareLayout {
  return ids.length <= COMBINED_DEFAULT_MAX_SERIES ? combineAll(ids) : separateAll(ids)
}

export function isCombined(layout: CompareLayout): boolean {
  return layout.length <= 1
}

export function isSeparated(layout: CompareLayout): boolean {
  return layout.every(group => group.length === 1)
}

/**
 * Fit a layout to the current selection: drop ids no longer selected, drop
 * charts left empty, and put newly selected ids into the first chart (or a
 * chart of their own when there are none).
 */
export function normalizeLayout(layout: CompareLayout, ids: string[]): CompareLayout {
  const selected = new Set(ids)
  const seen = new Set<string>()
  const groups = layout
    .map(group =>
      group.filter(id => {
        if (!selected.has(id) || seen.has(id)) return false
        seen.add(id)
        return true
      })
    )
    .filter(group => group.length > 0)

  const added = ids.filter(id => !seen.has(id))
  if (added.length === 0) return groups
  if (groups.length === 0) return [added]
  return [[...groups[0], ...added], ...groups.slice(1)]
}

// Move a series out of its chart into a new chart right after it. A series
// already alone stays put.
export function splitOut(layout: CompareLayout, id: string): CompareLayout {
  const index = layout.findIndex(group => group.includes(id))
  if (index === -1 || layout[index].length === 1) return layout
  const next = layout.map(group => group.filter(g => g !== id))
  next.splice(index + 1, 0, [id])
  return next
}

// Move a series into another chart; its old chart disappears if left empty.
export function moveInto(layout: CompareLayout, id: string, targetIndex: number): CompareLayout {
  const from = layout.findIndex(group => group.includes(id))
  if (from === -1 || from === targetIndex || !layout[targetIndex]) return layout
  return layout
    .map((group, i) => (i === targetIndex ? [...group, id] : group.filter(g => g !== id)))
    .filter(group => group.length > 0)
}

// Move a series into a new chart at the end (the "Add chart" placeholder);
// its old chart disappears if left empty.
export function moveToNewChart(layout: CompareLayout, id: string): CompareLayout {
  if (!layout.some(group => group.includes(id))) return layout
  return [...layout.map(group => group.filter(g => g !== id)).filter(group => group.length > 0), [id]]
}

// Remove a chart: its series join the chart above (the first chart's join the
// one below), so nothing is lost. The last chart can't be removed.
export function removeChart(layout: CompareLayout, index: number): CompareLayout {
  if (layout.length <= 1 || !layout[index]) return layout
  const target = index === 0 ? 1 : index - 1
  return layout
    .map((group, i) => (i === target ? [...group, ...layout[index]] : group))
    .filter((_, i) => i !== index)
}

/**
 * URL form: the chart number of each series, in selection order, dot-joined
 * ("0.0.1.2"). Series ids are long and device-specific; selection order is
 * already in the URL via `devices`, so positions are enough.
 */
export function encodeLayout(layout: CompareLayout, ids: string[]): string {
  const chartOf = new Map<string, number>()
  layout.forEach((group, i) => group.forEach(id => chartOf.set(id, i)))
  return ids.map(id => chartOf.get(id) ?? 0).join('.')
}

// Null when the param doesn't match the selection (a stale or edited link).
export function decodeLayout(param: string | null, ids: string[]): CompareLayout | null {
  if (!param) return null
  const parts = param.split('.')
  if (parts.length !== ids.length) return null
  const nums = parts.map(p => (/^\d{1,3}$/.test(p) ? parseInt(p, 10) : NaN))
  if (nums.some(n => Number.isNaN(n))) return null

  // Renumber charts 0..n-1 in chart-number order, so gaps ("0.5.5") still work
  const sorted = Array.from(new Set(nums)).sort((a, b) => a - b)
  const layout: CompareLayout = sorted.map(() => [])
  nums.forEach((n, i) => layout[sorted.indexOf(n)].push(ids[i]))
  return layout
}

// zf/zt are seconds, like from/to. Both must be valid and zf < zt.
export function parseZoomParams(zf: string | null, zt: string | null): ZoomWindow | null {
  const min = parseTimestamp(zf)
  const max = parseTimestamp(zt)
  if (min === undefined || max === undefined || min >= max) return null
  return { min, max }
}

export function serializeZoom(zoom: ZoomWindow): { zf: string; zt: string } {
  return {
    zf: String(Math.floor(zoom.min / 1000)),
    zt: String(Math.ceil(zoom.max / 1000)),
  }
}

// A zoom window only makes sense inside the fetched range.
export function zoomWithin(zoom: ZoomWindow | null, range: { minimum: number; maximum: number }): ZoomWindow | null {
  if (!zoom) return null
  if (zoom.max <= range.minimum || zoom.min >= range.maximum) return null
  return zoom
}

export interface YRange {
  minimum: number
  maximum: number
  reversed: boolean
}

// A "nice" tick step (1, 2, 2.5 or 5 × 10^n) so the shared axis reads cleanly.
function niceStep(span: number): number {
  const raw = span / 5
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
  const residual = raw / magnitude
  const factor = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 2.5 ? 2.5 : residual <= 5 ? 5 : 10
  return factor * magnitude
}

/**
 * One Y range across every series, so separate charts of the same sensor type
 * can be compared by height. Returns null when there is nothing to scale.
 */
export function sharedYRange(series: { readings: { value: number }[]; reversed: boolean }[]): YRange | null {
  let min = Infinity
  let max = -Infinity
  let reversed = false

  for (const s of series) {
    if (s.reversed) reversed = true
    for (const { value: v } of s.readings) {
      if (!Number.isFinite(v)) continue
      if (v < min) min = v
      if (v > max) max = v
    }
  }

  if (min === Infinity) return null
  if (min === max) {
    const pad = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 1
    min -= pad
    max += pad
  }

  const step = niceStep(max - min)
  return {
    minimum: Math.floor(min / step) * step,
    maximum: Math.ceil(max / step) * step,
    reversed,
  }
}
